/**
 * @module ServiceTestsScheduleGenerators
 * @description Phase 11 extraction of schedule option generators for interval and daily selectors.
 * Depends on user timezone preferences and shared schedule constants.
 */

import moment from 'moment-timezone';
import { has, max as lodashMax } from 'lodash';
import { getTimeZone } from '../../../stores/SessionStore';
import { getUserTimeFormat } from '../../../utilities/DateTimeUtilities';
import { ALL_TEST_INTERVALS, DEFAULT_MAX_INTERVAL_SECONDS } from './ServiceTestsScheduleConstants';

function getIntervalOverrides(min, max) {
    const filtered = ALL_TEST_INTERVALS.filter((opt) => opt.value >= min && opt.value <= max);
    // Fallback to all intervals if the range excludes every predefined value
    // (guards against invalid API data or unusual backend configurations)
    return filtered.length > 0 ? filtered : ALL_TEST_INTERVALS;
}

/**
 * Filter ALL_TEST_INTERVALS by the test type's allowed range.
 * Returns full list as fallback if range excludes all predefined intervals.
 * @param {{ min: number, max: number }|undefined} intervalRange - min/max in seconds
 * @param testType
 * @returns {Array<{ value: number, text: string }>}
 */
export function generateIntervals(intervalRange, testType) {
    // US9151, in order not to overschedule nPoint running BTT test, test intervals for BTT test is constraint to:
    // 1. not including 1 and 2 min, 2. intervals should be greater than 4 * maxTimeOut configured in test type.
    if (has(testType, 'bttTest') && testType.bttTest) {
        const maxTimeOut = testType.details.testTimeOutSec || 45;
        const min = Math.ceil(lodashMax([4 * maxTimeOut, 180]) / 60) * 60;
        let max = DEFAULT_MAX_INTERVAL_SECONDS;
        if (intervalRange) {
            max = intervalRange.max;
        }

        return getIntervalOverrides(min, max);
    }

    if (!intervalRange) {
        return ALL_TEST_INTERVALS;
    }
    const { min, max } = intervalRange;

    return getIntervalOverrides(min, max);
}

/**
 * Generate 96 time slots from 00:00 to 23:45 in 15-minute increments.
 * value: 'HH:mm' (24h stored format)
 * text: formatted per user's 12h/24h preference (e.g., '2:15 AM' or '02:15')
 * @returns {Array<{ value: string, text: string }>}
 */
export function generateTimes() {
    const userFormat = getUserTimeFormat();
    return Array.from({ length: 96 }, (_, i) => {
        const totalMinutes = i * 15;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        const text = moment({ hours, minutes }).format(userFormat);
        return { value, text };
    });
}

/**
 * Return the user's current timezone abbreviation (e.g. 'CST', 'UTC').
 * Used to build labels like "Start (CST)".
 * @returns {string}
 */
export function getUserTzAbbr() {
    return moment.tz(getTimeZone()).zoneAbbr();
}
